import EmptyState from '@/components/empty-state';
import StatusBadge from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ValidationErrors from '@/components/validation-errors';
import type { Milestone } from '@/types/milestone';
import {
  project_milestone_completion_path,
  project_milestone_path,
  project_milestone_positions_path,
  project_milestones_path,
} from '@/rails/routes';
import { Form, router } from '@inertiajs/react';
import { MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';

interface Props {
  projectId: string;
  milestones: Milestone[];
}

export default function RoadmapCard({ projectId, milestones }: Props) {
  const [adding, setAdding] = useState(false);
  const [milestoneStatus, setMilestoneStatus] = useState<Milestone['status']>('pending');

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= milestones.length) {
      return;
    }

    const ordered = milestones.map((milestone) => milestone.id);
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);

    router.patch(project_milestone_positions_path(projectId), {
      milestone_ids: ordered,
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-medium">
          Roadmap
          {milestones.length > 0 ? (
            <span className="text-muted-foreground ml-2 text-sm font-normal">
              {milestones.length} milestone{milestones.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </CardTitle>
        {!adding ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" />
            Add milestone
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding ? (
          <Form
            method="post"
            action={project_milestones_path(projectId)}
            className="bg-muted/40 space-y-3 rounded-lg border p-3"
            onSuccess={() => {
              setAdding(false);
              setMilestoneStatus('pending');
            }}
          >
            {({ errors, processing }) => (
              <>
                <div className="space-y-2">
                  <Label htmlFor="milestone_title">Title</Label>
                  <Input
                    id="milestone_title"
                    name="milestone[title]"
                    placeholder="Milestone title"
                    required
                    autoFocus
                  />
                  <ValidationErrors errors={errors.title} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="milestone_target_on">Target date</Label>
                    <Input
                      id="milestone_target_on"
                      name="milestone[target_on]"
                      type="date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="milestone_status">Status</Label>
                    <input type="hidden" name="milestone[status]" value={milestoneStatus} />
                    <Select
                      value={milestoneStatus}
                      onValueChange={(value) =>
                        setMilestoneStatus(value as Milestone['status'])
                      }
                    >
                      <SelectTrigger id="milestone_status" className="w-full">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={processing}>
                    Add
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAdding(false)}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>
        ) : null}

        {milestones.length === 0 && !adding ? (
          <EmptyState description="No milestones yet." className="py-2" />
        ) : null}

        {milestones.length > 0 ? (
          <ul className="divide-y">
            {milestones.map((milestone, index) => (
              <li
                key={milestone.id}
                className="flex items-start justify-between gap-3 py-3"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{milestone.title}</span>
                    <StatusBadge kind={milestone.status} />
                  </div>
                  {milestone.targetOn ? (
                    <p className="text-muted-foreground text-xs">Target {milestone.targetOn}</p>
                  ) : null}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {milestone.status !== 'done' ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        router.post(
                          project_milestone_completion_path(projectId, milestone.id)
                        )
                      }
                    >
                      Complete
                    </Button>
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" size="icon" variant="ghost" className="size-8">
                        <MoreHorizontal className="size-4" />
                        <span className="sr-only">Milestone actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={index === milestones.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        Move down
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Delete this milestone? This cannot be undone by AI.'
                            )
                          ) {
                            router.delete(
                              project_milestone_path(projectId, milestone.id)
                            );
                          }
                        }}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
